import { useState, useEffect } from 'react';
import { Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { ReactSVG } from 'react-svg';
import { Modal } from '@mui/material';
import { cn, convertIntoArray, getContentKeyAccToLang, showToast } from '../../../utils/utils';
import { bank_information_schema } from '../../../validations_schemas/bank_information_schema';
import { ErrorText, RadioButton, RowView, Text } from '../../CorporateForm/components';
import Dropdown from '../../../components/Dropdown';
import CustomInput from '../../../components/CustomInput';
import Button from '../../../components/Button';
import { fontSizes } from '../../../constants/constants';
import icons from '../../../constants/icons';
import dynamicfunction from '../../../utils/dynamicfunction';
import { useNavigate } from 'react-router';
import { logoutUser } from '../../../store/actions/authActions';
import { individual_bank_information_schema } from '../../../validations_schemas/individual_bank_information_schema';
import {
  addIndKsaBank,
  addIndKycKsaBank,
  addIndKycNonKsaBank,
  addIndNonKsaBank,
  deleteIndKsaBank,
  deleteIndKycKsaBank,
  deleteIndKycNonKsaBank,
  deleteIndNonKsaBank,
  editIndKsaBank,
  editIndKycKsaBank,
  editIndKycNonKsaBank,
  editIndNonKsaBank,
  getIndBankInformationData,
  getIndKsaBank,
  getIndKycKsaBank,
  getIndKycNonKsaBank,
  getIndNonKsaBank,
  postIndBankInformation,
  saveIndKycBankCustodianInformation,
} from '../../../store/actions/individualAction';
import { individual_bank_custodian_validation_schema } from '../../../validations_schemas/individual_bank_custodian_validation_schema';
import Loader from '../../../components/Loader';

const BankInformation = ({ onDone }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isArabic, TextWeight } = dynamicfunction();

  const { individualContent, language } = useSelector((state) => state.content);
  const { ind_post_loading, ind_bank_information_data, ind_bank_information_dropdown_data, ind_ksa_bank_list, ind_nonksa_bank_list, delete_bank_loading, bank_loading } = useSelector(
    (state) => state.individual,
  );

  const { client_application_id, is_kyc, applicant_type } = useSelector((state) => state.constant);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [banks, setBanks] = useState([]);
  const [selectedBank, setSelectedBank] = useState(null);
  const [bankLoadingIndex, setBankLoadingIndex] = useState(null);

  const is_ksa = applicant_type !== 'GCC National' && applicant_type !== 'Other National';

  const getContent = (key) => {
    return getContentKeyAccToLang(individualContent?.bank_custodian_info?.data?.attributes, key, isArabic);
  };

  const myArray = convertIntoArray(ind_bank_information_dropdown_data);

  useEffect(() => {
    getData();
  }, [language]);

  const getData = async () => {
    if (client_application_id) {
      await dispatch(getIndBankInformationData(client_application_id));

      if (is_kyc) {
        if (applicant_type !== 'GCC National' && applicant_type !== 'Other National') {
          await dispatch(
            getIndKycKsaBank(client_application_id, (data) => {
              setBanks(data);
            }),
          );
        } else {
          await dispatch(
            getIndKycNonKsaBank(client_application_id, (data) => {
              setBanks(data);
            }),
          );
        }
      } else {
        if (applicant_type !== 'GCC National' && applicant_type !== 'Other National') {
          await dispatch(
            getIndKsaBank(client_application_id, (data) => {
              setBanks(data);
            }),
          );
        } else {
          await dispatch(
            getIndNonKsaBank(client_application_id, (data) => {
              setBanks(data);
            }),
          );
        }
      }
    }
  }

  const onSubmit = (values) => {
    if (banks?.result?.length < 1 || banks?.length < 1) {
      showToast({
        icon: 'error',
        title: getContent('add_atleast_one_bank'),
      });
      return;
    }

    const api_data = {
      ...ind_bank_information_data,
      clientApplicationId: client_application_id,
      isHaveCustdoian: values?.isHaveCustdoian === '1' ? true : false,
      custodianName: values?.custodianName,
      accountAddress: values?.accountAddress,
      accountName: values?.accountName,
      accountNumber: values?.accountNumber,
      certificate: values?.certificate,
      certificateOther: values?.certificateOther,
      dividendsOtherIncome: values?.dividendsOtherIncome,
      dividendsOtherIncomeOther: values?.dividendsOtherIncomeOther,
      salesProceeds: values?.salesProceeds,
      salesProceedsOther: values?.salesProceedsOther,
      requestModel: banks,
    };

    if (is_ksa) {
      api_data.requestModel = banks?.map((item) => ({
        ...item,
        bankName: null,
        bankId: item?.bankId?.id,
      }));
    }

    dispatch(
      // if non saudi residence
      is_kyc
        ? saveIndKycBankCustodianInformation(api_data, () => {
          values?.isSaveExist ? navigate('/funds/available-funds') : onDone();
        })
        : postIndBankInformation(api_data, () => {
          values?.isSaveExist ? dispatch(logoutUser()) : onDone();
        }),
    );
  };

  const handleDeleteBank = (item, index) => {
    setBankLoadingIndex(index);
    if (is_kyc) {
      !is_ksa
        ? dispatch(
          deleteIndKycNonKsaBank(`${item?.internationalBankAccountId}&client_application_id=${client_application_id}`, () =>
            dispatch(getIndKycNonKsaBank(client_application_id)),
          ),
        )
        : dispatch(
          deleteIndKycKsaBank(`${item?.bankId}&client_application_id=${client_application_id}`, () =>
            dispatch(getIndKycKsaBank(client_application_id)),
          ),
        );
    } else {
      !is_ksa
        ? dispatch(
          deleteIndNonKsaBank(`${item?.internationalBankAccountId}&client_application_id=${client_application_id}`, () =>
            dispatch(getIndNonKsaBank(client_application_id, (data) => {
              setBanks(data);
            })),
          ),
        )
        : dispatch(
          deleteIndKsaBank(`${item?.bankId}&client_application_id=${client_application_id}`, () => dispatch(getIndKsaBank(client_application_id))),
        );
    }
  };

  const handleEditBank = (values, functions) => {
    const data = {
      branch: values?.branch,
      beneficiaryName: values?.beneficiaryName,
      iban: values?.iban?.replace(/-/g, ''),
      clientApplicationId: client_application_id,
    };

    if (!is_ksa) {
      data.internationalBankAccountId = values?.internationalBankAccountId;
      data.bankName = values?.bankName;
      // data.accountNumber = values?.accountNumber;
      data.registeredAddress = values?.registeredAddress;
      data.abaRoutingNumber = values?.abaRoutingNumber;
      data.swiftCode = values?.swiftCode;
    } else {
      data.bankId = values?.bankId?.id;
      data.ksaBankAccountId = values?.ksaBankAccountId;
    }
    if (is_kyc) {
      !is_ksa
        ? dispatch(editIndKycNonKsaBank(data, () => dispatch(getIndKycNonKsaBank(client_application_id))))
        : dispatch(editIndKycKsaBank(data, () => dispatch(getIndKycKsaBank(client_application_id))));
    } else {
      !is_ksa
        ? dispatch(editIndNonKsaBank(data, () => dispatch(getIndNonKsaBank(client_application_id, (data) => {
          setBanks(data);
        }))))
        : dispatch(editIndKsaBank(data, () => dispatch(getIndKsaBank(client_application_id))));
    }
  };

  const handleAddBank = (values, functions, resetForm) => {
    const exist = banks?.result?.find((bank) => bank?.iban === values?.iban);
    const swiftExist = !is_ksa && banks?.result?.find((bank) => bank?.swiftCode === values?.swiftCode);
    const cleanedIban = values.iban?.replace(/-/g, '');
    const data = {
      branch: values?.branch,
      beneficiaryName: values?.beneficiaryName,
      iban: cleanedIban,
      clientApplicationId: client_application_id,
    };

    if (is_ksa) {
      data.internationalBankAccountId = values?.internationalBankAccountId;
      data.ksaBankAccountId = 0;
      data.bankId = values?.bankId?.id;
    } else {
      data.internationalBankAccountId = 0;
      data.bankName = values?.bankName;
      data.registeredAddress = values?.registeredAddress;
      data.abaRoutingNumber = values?.abaRoutingNumber;
      data.swiftCode = values?.swiftCode;
    }

    if (exist) {
      showToast({
        icon: 'error',
        title: getContent('iban_already_exist'),
      });
      return;
    } else if (swiftExist) {
      showToast({
        icon: 'error',
        title: getContent('swift_code_already_exist'),
      });
      return;
    } else {
      if (is_kyc) {
        is_ksa
          ? dispatch(
            addIndKycKsaBank(data, () => {
              functions.resetForm();
              dispatch(getIndKycKsaBank(client_application_id));
            }),
          )
          : dispatch(
            addIndKycNonKsaBank(data, () => {
              functions.resetForm();
              dispatch(getIndKycNonKsaBank(client_application_id));
            }),
          )
      } else {
        is_ksa
          ? dispatch(
            addIndKsaBank(data, () => {
              functions.resetForm();
              dispatch(getIndKsaBank(client_application_id));
            }),
          )
          : dispatch(
            addIndNonKsaBank(data, () => {
              functions.resetForm();
              dispatch(getIndNonKsaBank(client_application_id, (data) => {
                setBanks(data);
              }));
            }),
          )
      }
    }

    setBanks(
      banks?.length > 0
        ? [...banks, { ...values, clientApplicationId: client_application_id }]
        : [{ ...values, clientApplicationId: client_application_id }],
    );
    resetForm();
  };
  const optionsYesOrNO = [
    { id: 1, name: getContent('yes') },
    { id: 0, name: getContent('no') },
  ];

  const getKsaBankName = (bankId) => {
    const bank = myArray?.bankList?.find((bank) => bank?.id?.toString() === bankId?.toString());
    return bank?.name ?? '';
  }

  return (
    <>
      {(banks?.result?.length < 5 || banks?.length < 5) && (
        <Formik
          initialValues={{
            ...bank_information_schema,
            isSaudi: is_ksa,
            iban: is_ksa ? 'SA' : '',
            abaRoutingNumber: '',
          }}
          validationSchema={individual_bank_information_schema}
          onSubmit={(values, functions) => {
            handleAddBank(values, functions);
          }}
          validateOnChange={true}
          validateOnBlur={true}
        >
          {({ handleSubmit, setFieldValue, values, resetForm, setFieldError, errors, touched, setFieldTouched }) => {
            return (
              <div>
                <RowView className='gap-3 justify-between mb-4'>
                  <Text
                    text={getContent(is_ksa ? 'for_banks_in_saudi' : 'for_banks_outside_saudi')}
                    color='text-black'
                    weight={TextWeight.semiBold}
                    isTruncate={false}
                    size={fontSizes.h22}
                  />
                  <div className='flex flex-col sm:flex-row justify-end gap-3'>
                    <Button
                      text={getContent('add')}
                      color={'text-white hover:text-primary '}
                      bgColor='bg-primary hover:bg-transparent hover:border-primary'
                      className={'border border-transparent w-44 h-fit px-0 text-center'}
                      onClick={handleSubmit}
                      isDisabled={bank_loading}
                      loading={bank_loading}
                    />
                    <Button
                      text={getContent('clear')}
                      color={'text-primary hover:text-white'}
                      bgColor='bg-white hover:bg-primary'
                      className={'border border-primary w-44 h-fit px-0 text-center'}
                      onClick={resetForm}
                    />
                  </div>
                </RowView>

                <RowView className='gap-7' isResponsive>
                  {is_ksa ? (
                    <Dropdown
                      label={getContent('bank_name')}
                      placeholder={getContent('enter_bank_name')}
                      selectedValue={values?.bankId}
                      valueKey='name'
                      onSelect={(val) => {
                        setFieldValue('bankId', val);
                        setFieldTouched('bankId', true);
                      }}
                      options={myArray?.bankList || []}
                      isRequired
                      errorMessage={getContent(touched?.bankId ? errors?.bankId : '')}
                    />
                  ) : (
                    <CustomInput
                      isRounded
                      isRequired={true}
                      maxLength={30}
                      label={getContent('bank_name')}
                      placeholder={getContent('enter_bank_name')}
                      value={values?.bankName}
                      onChange={(e) => {
                        setFieldValue('bankName', e.target.value);
                        setFieldTouched('bankName', true, false);
                      }}
                      errorMessage={getContent(touched?.bankName ? errors?.bankName : '')}
                    />
                  )}
                  <CustomInput
                    isRounded
                    isRequired={true}
                    maxLength={30}
                    label={getContent('branch')}
                    placeholder={getContent('enter_name')}
                    value={values?.branch}
                    onChange={(e) => {
                      setFieldValue('branch', e.target.value);
                      setFieldTouched('branch', true, false);
                    }}
                    errorMessage={getContent(touched?.branch ? errors?.branch : '')}
                  />
                  <CustomInput
                    isRounded
                    isRequired={true}
                    maxLength={30}
                    label={getContent('beneficiary_name')}
                    placeholder={getContent('enter_name')}
                    value={values?.beneficiaryName}
                    onChange={(e) => {
                      setFieldValue('beneficiaryName', e.target.value);
                      setFieldTouched('beneficiaryName', true, false);
                    }}
                    errorMessage={getContent(touched?.beneficiaryName ? errors?.beneficiaryName : '')}
                  />
                </RowView>

                <RowView className='gap-7 pt-3 sm:pt-5' isResponsive>
                  <CustomInput
                    isRounded
                    isRequired={true}
                    label={getContent('main_account_number')}
                    labelDivClass={'h-6 md:h-9 lg:h-6'}
                    placeholder={getContent('enter_iban')}
                    value={values?.iban}
                    onChange={(e) => {
                      if (!e.target.value.startsWith('SA') && values?.isSaudi) {
                        e.target.value = 'SA';
                      }
                      setFieldValue('iban', e.target.value?.toUpperCase());
                      setFieldTouched('iban', true, false);
                    }}
                    errorMessage={getContent(touched?.iban ? errors?.iban : '')}
                    maxLength={values?.isSaudi ? 24 : 34}
                  />
                  {!is_ksa && (
                    <>
                      <CustomInput
                        isRounded
                        isRequired={true}
                        maxLength={50}
                        label={getContent('registered_address')}
                        labelDivClass={'h-6 md:h-9 lg:h-6'}
                        placeholder={getContent('enter_address')}
                        value={values?.registeredAddress}
                        onChange={(e) => {
                          setFieldValue('registeredAddress', e.target.value);
                          setFieldTouched('registeredAddress', true, false);
                        }}
                        errorMessage={getContent(touched?.registeredAddress ? errors?.registeredAddress : '')}
                      />
                      <CustomInput
                        isRounded
                        isRequired={true}
                        label={getContent('aba_routing_no')}
                        labelDivClass={'h-6 md:h-9 lg:h-6'}
                        placeholder={getContent('enter_number')}
                        value={values?.abaRoutingNumber}
                        onChange={(e) => {
                          if (/^\d*$/.test(e.target.value)) {
                            setFieldValue('abaRoutingNumber', e.target.value);
                            setFieldTouched('abaRoutingNumber', true, false);
                          }
                        }}
                        maxLength={50}
                        errorMessage={getContent(touched?.abaRoutingNumber ? errors?.abaRoutingNumber : '')}
                      />
                    </>
                  )}
                </RowView>

                {!is_ksa && (
                  <RowView className='gap-7 pt-3 sm:pt-5' isResponsive>
                    <CustomInput
                      isRounded
                      isRequired={true}
                      label={getContent('swift_code')}
                      placeholder={getContent('enter_swift_code')}
                      value={values?.swiftCode}
                      onChange={(e) => {
                        setFieldValue('swiftCode', e.target.value?.toUpperCase());
                        setFieldTouched('swiftCode', true, false);
                      }}
                      errorMessage={getContent(touched?.swiftCode ? errors?.swiftCode : '')}
                      maxLength={11}
                    />
                    <div className='lg:w-full hidden lg:block'></div>
                    <div className='lg:w-full hidden lg:block'></div>
                  </RowView>
                )}
              </div>
            );
          }}
        </Formik>
      )}

      {(is_ksa ? ind_ksa_bank_list : ind_nonksa_bank_list)?.length > 0 && (
        <div className='border border-border rounded-lg mt-4 min-w-full overflow-x-auto'>
          <RowView className='bg-primary/10 px-4 py-3 gap-2 justify-between w-max min-w-full'>
            <RowView className={'gap-2'}>
              <Text
                text={getContent('bank_name')}
                color='text-primary'
                className='min-w-[150px]'
                weight={TextWeight.medium}
                isTruncate={false}
                size={fontSizes.h14}
              />
              <Text
                text={getContent('branch')}
                color='text-primary'
                className='min-w-[150px]'
                weight={TextWeight.medium}
                isTruncate={false}
                size={fontSizes.h14}
              />
              <Text
                text={getContent('beneficiary_name')}
                color='text-primary'
                className='min-w-[200px]'
                weight={TextWeight.medium}
                isTruncate={false}
                size={fontSizes.h14}
              />
              <Text
                text={getContent('main_account_number')}
                color='text-primary'
                className='min-w-[300px] max-w-[300px]'
                weight={TextWeight.medium}
                isTruncate={false}
                size={fontSizes.h14}
              />
              {!is_ksa && (
                <>
                  <Text
                    text={getContent('registered_address')}
                    color='text-primary'
                    className='min-w-[300px] max-w-[300px]'
                    weight={TextWeight.medium}
                    isTruncate={false}
                    size={fontSizes.h14}
                  />
                  <Text
                    text={getContent('aba_routing_no')}
                    color='text-primary'
                    className='min-w-[300px] max-w-[300px]'
                    weight={TextWeight.medium}
                    isTruncate={false}
                    size={fontSizes.h14}
                  />
                  <Text
                    text={getContent('swift_code')}
                    color='text-primary'
                    className='min-w-[150px] max-w-[150px]'
                    weight={TextWeight.medium}
                    isTruncate={false}
                    size={fontSizes.h14}
                  />
                </>
              )}
            </RowView>
            <Text
              text={getContent('action')}
              color='text-primary'
              align={cn(isArabic ? 'mr-auto text-left' : 'ml-auto text-right')}
              className={'min-w-[60px] max-w-[60px]'}
              weight={TextWeight.medium}
              isTruncate={false}
              size={fontSizes.h14}
            />
          </RowView>
          {(is_ksa ? ind_ksa_bank_list : ind_nonksa_bank_list)?.map((bank, index) => (
            <RowView key={index} className={'px-4 py-3 border-b border-b-border gap-2 justify-between w-max min-w-full'}>
              <RowView className={'gap-2'}>
                <Text
                  text={bank?.bankId ? getKsaBankName(bank?.bankId) : bank?.bankName}
                  color='text-primary'
                  className='min-w-[150px] max-w-[150px] line-clamp-2'
                  weight={TextWeight.medium}
                  isTruncate={false}
                  size={fontSizes.h14}
                />
                <Text
                  text={bank?.branch}
                  color='text-primary'
                  className='min-w-[150px] max-w-[150px] line-clamp-2'
                  weight={TextWeight.medium}
                  isTruncate={false}
                  size={fontSizes.h14}
                />
                <Text
                  text={bank?.beneficiaryName}
                  color='text-primary'
                  className='min-w-[200px] max-w-[200px] line-clamp-2'
                  weight={TextWeight.medium}
                  isTruncate={false}
                  size={fontSizes.h14}
                />
                <Text
                  text={bank?.iban}
                  color='text-primary'
                  className='min-w-[300px] w-[300px]'
                  weight={TextWeight.medium}
                  isTruncate={false}
                  size={fontSizes.h14}
                />
                {!is_ksa && (
                  <>
                    <Text
                      text={bank?.registeredAddress}
                      color='text-primary'
                      className='min-w-[300px] w-[300px]'
                      weight={TextWeight.medium}
                      isTruncate={false}
                      size={fontSizes.h14}
                    />
                    <Text
                      text={bank?.abaRoutingNumber}
                      color='text-primary'
                      className='min-w-[300px] w-[300px]'
                      weight={TextWeight.medium}
                      isTruncate={false}
                      size={fontSizes.h14}
                    />
                    <Text
                      text={bank?.swiftCode}
                      color='text-primary'
                      className='min-w-[150px] max-w-[150px]'
                      weight={TextWeight.medium}
                      isTruncate={false}
                      size={fontSizes.h14}
                    />
                  </>
                )}
              </RowView>
              <RowView className='min-w-[60px] max-w-[60px] justify-center gap-4 flex-shrink-0'>
                {delete_bank_loading && bankLoadingIndex === index ? <Loader width={"w-4 h-4"} /> : <ReactSVG src={icons.delete_icon} className='cursor-pointer' onClick={() => handleDeleteBank(bank, index)} />}
                <ReactSVG
                  src={icons.edit_icon}
                  className='cursor-pointer'
                  onClick={() => {
                    setSelectedBank({ ...bank, index });
                    setIsModalOpen(true);
                  }}
                />
              </RowView>
            </RowView>
          ))}
        </div>
      )}

      <Formik
        enableReinitialize
        initialValues={{
          isHaveCustdoian:
            ind_bank_information_data?.result?.custodianInputs?.isHaveCustdoian === true
              ? '1'
              : ind_bank_information_data?.result?.custodianInputs?.isHaveCustdoian === false
                ? '0'
                : '',
          // isHaveCustdoian:
          //   ind_bank_information_data?.isHaveCustdoian
          //     ? 1
          //     : 0,
          custodianName: ind_bank_information_data?.result?.custodianInputs?.custodianName ?? '',
          accountAddress: ind_bank_information_data?.result?.custodianInputs?.accountAddress ?? '',
          accountName: ind_bank_information_data?.result?.custodianInputs?.accountName ?? '',
          accountNumber: ind_bank_information_data?.result?.custodianInputs?.accountNumber ?? '',

          // certificate: '',
          // certificateOther: '',
          // dividendsOtherIncome: '',
          // dividendsOtherIncomeOther: '',
          // salesProceeds: '',
          // salesProceedsOther: '',
          isSaveExist: false,
        }}
        context={{ is_ksa }}
        onSubmit={(values) => {
          onSubmit(values);
        }}
        validationSchema={individual_bank_custodian_validation_schema}
        validateOnChange={true}
        validateOnBlur={true}
      >
        {({ handleSubmit, setFieldValue, values, touched, setFieldTouched, errors }) => {
          return (
            <>
              <div className='py-3 sm:py-5'>
                <Text text={getContent('custodian_info')} size={fontSizes.h22} isTruncate={false} weight={TextWeight.semiBold} className={'block'} />
              </div>
              <div>
                <RadioButton
                  label={getContent('is_have_custodian')}
                  required={false}
                  value={values?.isHaveCustdoian}
                  onChange={(e) => {
                    setFieldValue('isHaveCustdoian', e);
                    setFieldTouched('isHaveCustdoian', false);
                  }}
                  options={optionsYesOrNO}
                />
                <ErrorText text={getContent(touched?.isHaveCustdoian ? errors?.isHaveCustdoian : '')} />
              </div>
              {values?.isHaveCustdoian?.toString() === '1' && (
                <div>
                  <RowView className='gap-7 pt-3 sm:pt-5' isResponsive>
                    <CustomInput
                      isRounded
                      isRequired={true}
                      maxLength={30}
                      label={getContent('custodian_name')}
                      placeholder={getContent('enter_name')}
                      value={values?.custodianName}
                      onChange={(e) => {
                        setFieldValue('custodianName', e.target.value);
                        setFieldTouched('custodianName', true, false);
                      }}
                      errorMessage={getContent(touched?.custodianName ? errors?.custodianName : '')}
                    />
                    <CustomInput
                      isRounded
                      isRequired={true}
                      maxLength={150}
                      label={getContent('custodian_address')}
                      placeholder={getContent('enter_address')}
                      value={values?.accountAddress}
                      onChange={(e) => {
                        setFieldValue('accountAddress', e.target.value);
                        setFieldTouched('accountAddress', true, false);
                      }}
                      errorMessage={getContent(touched?.accountAddress ? errors?.accountAddress : '')}
                    />
                    <CustomInput
                      isRounded
                      isRequired={true}
                      maxLength={50}
                      label={getContent('acc_name')}
                      placeholder={getContent('enter_name')}
                      value={values?.accountName}
                      onChange={(e) => {
                        setFieldValue('accountName', e.target.value);
                        setFieldTouched('accountName', true, false);
                      }}
                      errorMessage={getContent(touched?.accountName ? errors?.accountName : '')}
                    />
                  </RowView>
                  <RowView className='gap-7 pt-3 sm:pt-5' isResponsive>
                    <CustomInput
                      isRounded
                      isRequired={true}
                      maxLength={20}
                      label={getContent('acc_no')}
                      placeholder={getContent('enter_number')}
                      value={values?.accountNumber}
                      onChange={(e) => {
                        setFieldValue('accountNumber', e.target.value);
                        setFieldTouched('accountNumber', true, false);
                      }}
                      errorMessage={getContent(touched?.accountNumber ? errors?.accountNumber : '')}
                    />
                    <div className='lg:w-full hidden lg:block'></div>
                    <div className='lg:w-full hidden lg:block'></div>
                  </RowView>
                </div>
              )}

              {/* {!is_ksa && (
                <>
                  <div className='py-1 sm:py-3'>
                    <Text
                      text={getContent('indicate_where_forward')}
                      size={fontSizes.h14}
                      isTruncate={false}
                      weight={TextWeight.medium}
                      className={'block'}
                      isRequired={true}
                    />
                  </div>
                  <RowView className='gap-7 pt-3 sm:pt-5' isResponsive>
                    <div className='w-full'>
                      <RadioButton
                        label={getContent('certificates')}
                        required={false}
                        value={values?.certificate}
                        onChange={(e) => {
                          setFieldValue('certificate', e);
                          setFieldTouched('certificate', false);
                        }}
                        options={myArray?.certificateRadioBtn}
                      />
                      <ErrorText
                        text={getContent(
                          touched?.certificate ? errors?.certificate : ''
                        )}
                      />
                      {Number(values?.certificate) === 803 && (
                        <CustomInput
                          isRounded
                          isRequired={true}
                          label={getContent('other_please_specify')}
                          placeholder={getContent('specify_other_certificate_recipient')}
                          value={values?.certificateOther}
                          onChange={(e) => {
                            setFieldValue('certificateOther', e.target.value);
                            setFieldTouched('certificateOther', true, false);
                          }}
                          errorMessage={getContent(
                            touched?.certificateOther
                              ? errors?.certificateOther
                              : ''
                          )}
                        />
                      )}
                    </div>
                    <div className='w-full'>
                      <RadioButton
                        label={getContent('dividends_other_income')}
                        required={false}
                        value={values?.dividendsOtherIncome}
                        onChange={(e) => {
                          setFieldValue('dividendsOtherIncome', e);
                          setFieldTouched('dividendsOtherIncome', false);
                        }}
                        options={myArray?.dividendsRadioBtn}
                      />
                      <ErrorText
                        text={getContent(
                          touched?.dividendsOtherIncome
                            ? errors?.dividendsOtherIncome
                            : ''
                        )}
                      />
                      {Number(values?.dividendsOtherIncome) === 803 && (
                        <CustomInput
                          isRounded
                          isRequired={true}
                          label={getContent('other_please_specify')}
                          placeholder={getContent('specify_other_dividend_recipient')}
                          value={values?.dividendsOtherIncomeOther}
                          onChange={(e) => {
                            setFieldValue(
                              'dividendsOtherIncomeOther',
                              e.target.value
                            );
                            setFieldTouched(
                              'dividendsOtherIncomeOther',
                              true,
                              false
                            );
                          }}
                          errorMessage={getContent(
                            touched?.certificateOther
                              ? errors?.certificateOther
                              : ''
                          )}
                        />
                      )}
                    </div>
                    <div className='w-full'>
                      <RadioButton
                        label={getContent('sales_proceed')}
                        required={false}
                        value={values?.salesProceeds}
                        onChange={(e) => {
                          setFieldValue('salesProceeds', e);
                          setFieldTouched('salesProceeds', false);
                        }}
                        options={myArray?.salesRadioBtn}
                      />
                      <ErrorText
                        text={getContent(
                          touched?.salesProceeds ? errors?.salesProceeds : ''
                        )}
                      />
                      {Number(values?.salesProceeds) === 803 && (
                        <CustomInput
                          isRounded
                          isRequired={true}
                          label={getContent('other_please_specify')}
                          placeholder={getContent('specify_other_sales_recipient')}
                          value={values?.salesProceedsOther}
                          onChange={(e) => {
                            setFieldValue('salesProceedsOther', e.target.value);
                            setFieldTouched('salesProceedsOther', true, false);
                          }}
                          errorMessage={getContent(
                            touched?.salesProceedsOther
                              ? errors?.salesProceedsOther
                              : ''
                          )}
                        />
                      )}
                    </div>
                  </RowView>
                </>
              )} */}

              <div className='w-full h-0.5 bg-border mt-3 sm:mt-5'></div>
              <RowView className='gap-3 sm:gap-7 pt-3 sm:pt-5'>
                <Button
                  text={getContent('save_proceed')}
                  color={'text-white hover:text-primary'}
                  bgColor='bg-primary hover:bg-transparent hover:border-primary'
                  className={'border border-transparent w-44 px-0 text-center'}
                  type={'submit'}
                  isDisabled={ind_post_loading}
                  loading={ind_post_loading}
                  onClick={() => {
                    setFieldValue('isSaveExist', false);
                    handleSubmit();
                  }}
                />
                <Button
                  text={getContent('save_exit')}
                  color={'text-primary hover:text-white'}
                  bgColor='bg-white hover:bg-primary'
                  className={'border border-primary w-44 px-0 text-center'}
                  isDisabled={ind_post_loading}
                  onClick={() => {
                    setFieldValue('isSaveExist', true);
                    handleSubmit();
                  }}
                />
              </RowView>
            </>
          );
        }}
      </Formik>

      <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <div className='bg-white rounded-lg p-6 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-11/12 md:w-1/2'>
          <Formik
            enableReinitialize
            initialValues={{
              internationalBankAccountId: selectedBank?.internationalBankAccountId || null,
              bankName: selectedBank?.bankName || null,
              bankId: selectedBank?.bankId ? myArray?.bankList?.find((bank) => bank?.id?.toString() === selectedBank?.bankId?.toString()) : null,
              branch: selectedBank?.branch || null,
              beneficiaryName: selectedBank?.beneficiaryName || null,
              iban: selectedBank?.iban || null,
              registeredAddress: selectedBank?.registeredAddress || null,
              abaRoutingNumber: selectedBank?.abaRoutingNumber || null,
              swiftCode: selectedBank?.swiftCode || null,
              isSaudi: is_ksa,
            }}
            validationSchema={individual_bank_information_schema}
            onSubmit={(values, functions) => {
              handleEditBank(values, functions);
              // setBanks(updatedBanks);
              setIsModalOpen(false);
              setSelectedBank(null);
              functions.resetForm();
            }}
            validateOnChange={true}
            validateOnBlur={true}
          >
            {({ handleSubmit, setFieldValue, values, resetForm, setFieldError, touched, setFieldTouched, errors }) => {
              return (
                <div>
                  <RowView className='item-center justify-between'>
                    <Text text={getContent('edit')} weight={TextWeight.bold} size={fontSizes.h20} />
                    <ReactSVG src={icons.cross} className='cursor-pointer' onClick={() => setIsModalOpen(false)} />
                  </RowView>

                  <RowView className='gap-7 mt-4' isResponsive>
                    {is_ksa ? (
                      <Dropdown
                        isRequired
                        label={getContent('bank_name')}
                        placeholder={getContent('please_select')}
                        selectedValue={values?.bankId}
                        valueKey='name'
                        onSelect={(val) => {
                          setFieldValue('bankId', val);
                          setFieldTouched('bankId', true, false);
                        }}
                        options={myArray?.bankList || []}
                        errorMessage={getContent(touched?.bankId ? errors?.bankId : '')}
                      />
                    ) : (
                      <CustomInput
                        isRounded
                        isRequired={true}
                        maxLength={30}
                        label={getContent('bank_name')}
                        placeholder={getContent('enter_bank_name')}
                        value={values?.bankName}
                        onChange={(e) => {
                          setFieldValue('bankName', e.target.value);
                          setFieldTouched('bankName', true, false);
                        }}
                        errorMessage={getContent(touched?.bankName ? errors?.bankName : '')}
                      />
                    )}
                    <CustomInput
                      isRounded
                      isRequired={true}
                      maxLength={30}
                      label={getContent('branch')}
                      placeholder={getContent('enter_name')}
                      value={values?.branch}
                      onChange={(e) => {
                        setFieldValue('branch', e.target.value);
                        setFieldTouched('branch', true, false);
                      }}
                      errorMessage={getContent(touched?.branch ? errors?.branch : '')}
                    />
                  </RowView>

                  <RowView className='gap-7 pt-3 sm:pt-5' isResponsive>
                    <CustomInput
                      isRounded
                      isRequired={true}
                      maxLength={30}
                      label={getContent('beneficiary_name')}
                      labelDivClass={'sm:h-6 md:h-9 xl:h-6'}
                      placeholder={getContent('enter_name')}
                      value={values?.beneficiaryName}
                      onChange={(e) => {
                        setFieldValue('beneficiaryName', e.target.value);
                        setFieldTouched('beneficiaryName', true, false);
                      }}
                      errorMessage={getContent(touched?.beneficiaryName ? errors?.beneficiaryName : '')}
                    />
                    <CustomInput
                      isRounded
                      isRequired={true}
                      label={getContent('main_account_number')}
                      labelDivClass={'sm:h-6 md:h-9 xl:h-6'}
                      placeholder={getContent('enter_iban')}
                      value={values?.iban}
                      onChange={(e) => {
                        if (!e.target.value.startsWith('SA') && values?.isSaudi) {
                          e.target.value = 'SA';
                        }
                        setFieldValue('iban', e.target.value?.toUpperCase());
                        setFieldTouched('iban', true, false);
                      }}
                      errorMessage={getContent(touched?.iban ? errors?.iban : '')}
                      maxLength={values?.isSaudi ? 24 : 32}
                    />
                  </RowView>

                  {!is_ksa && (
                    <RowView className='gap-7 pt-3 sm:pt-5' isResponsive>
                      <CustomInput
                        isRounded
                        isRequired={true}
                        maxLength={50}
                        label={getContent('registered_address')}
                        labelDivClass={'sm:h-6 md:h-9 xl:h-6'}
                        placeholder={getContent('enter_address')}
                        value={values?.registeredAddress}
                        onChange={(e) => {
                          setFieldValue('registeredAddress', e.target.value);
                          setFieldTouched('registeredAddress', true, false);
                        }}
                        errorMessage={getContent(touched?.registeredAddress ? errors?.registeredAddress : '')}
                      />
                      <CustomInput
                        isRounded
                        isRequired={true}
                        maxLength={50}
                        label={getContent('aba_routing_no')}
                        labelDivClass={'sm:h-6 md:h-9 xl:h-6'}
                        placeholder={getContent('enter_number')}
                        value={values?.abaRoutingNumber}
                        onChange={(e) => {
                          setFieldValue('abaRoutingNumber', e.target.value);
                          setFieldTouched('abaRoutingNumber', true, false);
                        }}
                        errorMessage={getContent(touched?.abaRoutingNumber ? errors?.abaRoutingNumber : '')}
                      />
                    </RowView>
                  )}

                  {!is_ksa && (
                    <RowView className='gap-7 pt-3 sm:pt-5' isResponsive>
                      <CustomInput
                        isRounded
                        isRequired={true}
                        label={getContent('swift_code')}
                        placeholder={getContent('enter_swift_code')}
                        value={values?.swiftCode}
                        onChange={(e) => {
                          setFieldValue('swiftCode', e.target.value?.toUpperCase());
                          setFieldTouched('swiftCode', true, false);
                        }}
                        errorMessage={getContent(touched?.swiftCode ? errors?.swiftCode : '')}
                        maxLength={11}
                      />
                    </RowView>
                  )}
                  <RowView className='gap-7 pt-3 sm:pt-5'>
                    <Button
                      text={getContent('submit')}
                      color={'text-white hover:text-primary '}
                      bgColor='bg-primary hover:bg-transparent hover:border-primary'
                      className={'border border-transparent w-1/3 mx-auto px-0 text-center'}
                      onClick={handleSubmit}
                      isDisabled={bank_loading}
                      loading={bank_loading}
                    />
                  </RowView>
                </div>
              );
            }}
          </Formik>
        </div>
      </Modal>
    </>
  );
};

export default BankInformation;
